<script lang="ts">

  import { goto } from "$app/navigation"
  import httpClient from "$lib/axios-client"
  import { page } from "$app/state"
  import { Button } from "$lib/components/ui/button"
  import { CalendarMonth } from "$lib/components/ui/calendar-month"
  import { Card, CardContent, CardHeader, CardTitle } from "$lib/components/ui/card"
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from "$lib/components/ui/dropdown-menu"
  import { Popover, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import type { _response } from "$lib/data/type/_response"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import CalendarIcon from "@lucide/svelte/icons/calendar"
  import EllipsisVerticalIcon from "@lucide/svelte/icons/ellipsis-vertical"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import PlusIcon from "@lucide/svelte/icons/plus"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery, createQuery } from "@tanstack/svelte-query"
  import Navigation from "../../(main)/_components/navigation.svelte"
  import DialogConfirmDelete from "./_components/dialog-confirm-delete.svelte"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import DialogForm from "./_components/dialog-form.svelte"
  import type { Order } from "./_types"

  type Dash = {
    count: number
    total_outstanding: number
  }
  const prefix = $derived(page.params.prefix ?? "0")

  const initialFilterDate = today(getLocalTimeZone())
  let filterDate = $state<CalendarDate>(initialFilterDate)

  let sentinel = $state<HTMLDivElement>()

  let select = $state("")
  const detailDialog = useDialog()
  const deleteDialog = useDialog()
  const formDialog = useDialog()

  const filterDateLabel = $derived(filterDate.format("MMMM yyyy"))

  const dash = createQuery(() => ({
    queryKey: ["order-outstanding-dash", {
      prefix,
      date: filterDate.toString()
    }],
    queryFn: async () => {
      const response = await httpClient.get<_response<Dash>>(`/api/${prefix}/order/data/dash`, {
        params: {
          date: filterDate.toString()
        }
      })

      return response.data.result!
    }
  }))

  const query = createInfiniteQuery(() => ({
    queryKey: ["order-outstanding", {
      prefix,
      date: filterDate.toString()
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Order[]>>(`/api/${prefix}/order/data`, {
        params: {
          page: pageParam,
          date: filterDate.toString()
        }
      })

      return response.data.result!
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))


  const rows = $derived(query.data?.pages.flat() ?? [])

  function handleRefresh() {
    return Promise.all([query.refetch(), dash.refetch()])
  }

  function handleDetail(id: number) {
    select = String(id)
    detailDialog.setOpen(true)
  }

  function handleAdd() {
    select = "create"
    formDialog.setOpen(true)
  }

  function handleEdit(id: number) {
    void goto(`/${prefix}/order/${id}`)
  }

  function handleDelete(id: number) {
    select = String(id)
    deleteDialog.setOpen(true)
  }

  $effect(() => {
    if (!sentinel || !query.hasNextPage || query.isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void query.fetchNextPage()
      },
      { threshold: 0.5 },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Order</h1>
      </div>
      <div></div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group shrink-0"
        aria-label="Muat ulang order"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={() => void handleRefresh()}>
        <LoaderCircleIcon
          class="invisible absolute animate-spin group-data-[loading=true]:visible"
          aria-hidden="true"/>
        <RefreshCwIcon
          class="group-data-[loading=true]:invisible"
          aria-hidden="true"/>
      </Button>
      <Popover>
        <PopoverTrigger>
          {#snippet child({ props })}
            <Button
              {...props}
              variant="outline"
              class="min-w-48 justify-start whitespace-nowrap text-left font-normal"
              aria-label="Pilih bulan order">
              <CalendarIcon aria-hidden="true"/>
              <span class="tabular-nums">{filterDateLabel}</span>
            </Button>
          {/snippet}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          class="w-72 p-0">
          <CalendarMonth
            selected={filterDate}
            onSelect={(value) => {
              filterDate = value
              document.keyEsc()
            }}/>
        </PopoverContent>
      </Popover>
    </div>
  </nav>
  <div class="min-h-full">
    <div class="h-3"></div>
    <div class="mx-3 grid grid-cols-2 gap-3">
      <Card
        size="sm"
        class="gap-0 py-0 shadow-none">
        <CardHeader class="px-2 pt-2 pb-0">
          <CardTitle class="line-clamp-1 text-xs font-normal leading-none">Order</CardTitle>
        </CardHeader>
        <CardContent class="px-2 pb-2 text-xl font-semibold tabular-nums tracking-tight">
          {dash.data?.count ?? 0}
        </CardContent>
      </Card>
      <Card
        size="sm"
        class="gap-0 py-0 shadow-none">
        <CardHeader class="px-2 pt-2 pb-0">
          <CardTitle class="line-clamp-1 text-xs font-normal leading-none">Total Outstanding</CardTitle>
        </CardHeader>
        <CardContent class="px-2 pb-2 text-xl font-semibold tabular-nums tracking-tight">
          Rp{dash.data?.total_outstanding?.toLocaleString("en-US") ?? "0"}
        </CardContent>
      </Card>
    </div>
    <div class="h-3"></div>
    {#if query.isFetching && rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Memuat...</div>
    {:else if query.isError}
      <div
        class="flex flex-col items-center gap-2 px-3 py-4 text-center text-xs"
        role="alert">
        <div>Order gagal dimuat</div>
        <Button
          variant="secondary"
          onclick={() => void handleRefresh()}>
          Coba lagi
        </Button>
      </div>
    {:else if rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {:else}
      {#each rows as order (order.id)}
        <div class="flex items-center gap-3 border-b px-3 py-2 text-sm hover:bg-muted">
          <div class="flex-1">
            <div class="flex items-center gap-1 text-xs text-muted-foreground">
              <div class="tabular-nums tracking-tight">{order.date.toDateFormat("dd/MM/yyyy")}</div>
              <div class="text-[0.5rem]">|</div>
              <div class="tabular-nums tracking-tight">{order.number}</div>
              <div class="flex-1"></div>
              <div>{order.status.name}</div>
            </div>
            <div>{order.customer.name}</div>
            <div class="tabular-nums tracking-tight">{order.amount.toLocaleString("en-US")}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  variant="ghost"
                  class="px-1"
                  aria-label={`Aksi ${order.number}`}>
                  <EllipsisVerticalIcon aria-hidden="true"/>
                </Button>
              {/snippet}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onclick={() => handleDetail(order.id)}>Detail</DropdownMenuItem>
              <DropdownMenuItem
                disabled={order.status.id !== ""}
                onclick={() => handleEdit(order.id)}>
                Order
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={order.status.id !== ""}
                onclick={() => handleDelete(order.id)}>
                Hapus
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      {/each}
    {/if}
    <div
      bind:this={sentinel}
      class="h-20">
    </div>
    <div class="h-16"></div>
  </div>
  <div class="fixed inset-x-0 bottom-16 z-40 mx-auto flex w-full max-w-3xl justify-end pb-4 pe-4">
    <Button
      variant="default"
      size="icon"
      class="size-12 rounded-full p-0 shadow"
      aria-label="Tambah order"
      onclick={handleAdd}>
      <PlusIcon
        class="!size-6"
        aria-hidden="true"/>
    </Button>
  </div>
  <Navigation/>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={select}/>
<DialogForm
  open={formDialog.state}
  setOpen={formDialog.setOpen}
  select={select}
  refresh={() => void handleRefresh()}/>
<DialogConfirmDelete
  open={deleteDialog.state}
  setOpen={deleteDialog.setOpen}
  select={select}/>
